import { useEffect, useState } from "react";
import { getDraftProfile } from "../../db";
import { PROFILE_BASE } from "../../profiles";
import { resolveDraftProfile } from "../../profiles/resolve-draft";
import type { ResolvedSourceProfile } from "../../profiles/resolve";
import { useEffectiveProfile, type EffectiveProfileState } from "./use-effective-profile";

/**
 * Resolve the profile a completed run was analyzed under, committed or draft.
 *
 * `useEffectiveProfile` is repo-only by design, and a view that used it alone
 * would tell someone their draft run's profile "is not registered in this
 * build" — true of the repo, and useless to them. This falls back to the stored
 * draft, so a run reads back under the policy that actually governed it.
 *
 * The repo lookup still wins. A draft id can never shadow a committed profile.
 */
export function useRunProfile(profileId: string | null): EffectiveProfileState {
  const repo = useEffectiveProfile(profileId);
  const [draft, setDraft] = useState<{ key: string; profile: ResolvedSourceProfile | null }>({
    key: "",
    profile: null
  });

  const needsDraft = profileId !== null && repo.profile === null;

  useEffect(() => {
    if (!needsDraft || !profileId) return;
    let cancelled = false;
    void getDraftProfile(profileId).then((row) => {
      if (cancelled) return;
      setDraft({
        key: profileId,
        profile:
          row && PROFILE_BASE
            ? resolveDraftProfile(PROFILE_BASE, row.delta, {
                measuredAt: row.createdAt,
                baselineFileName: row.baselineFileName,
                latestFileName: row.latestFileName
              })
            : null
      });
    });
    return () => {
      cancelled = true;
    };
  }, [needsDraft, profileId]);

  if (!needsDraft) return repo;

  const settled = draft.key === profileId;
  return {
    ...repo,
    profile: settled ? draft.profile : null,
    // A draft is never overridable, so those flags stay false; `loading` holds
    // the caller off until the draft read settles, exactly as it does for an
    // override read, so no run starts or renders under an unsettled policy.
    overrideActive: false,
    overrideStale: false,
    loading: !settled
  };
}
